import { isSameMobile, toLocalMobile } from 'utilities/phone'

export interface IdentityForm {
    firstName: string
    lastName: string
    phone: string
}

export type IdentityInputError =
    | 'name'
    | 'first_name'
    | 'last_name'
    | 'phone_number'

export type IdentityChanges =
    | { ok: true; changes: IMemberIdentityChanges }
    | { ok: false; error: IdentityInputError }

/** `Member.first_name` / `last_name` on the backend. */
const NAME_MAX = 100

/** The backend folds whitespace the same way before it compares. */
const tidy = (value: string) => value.trim().replace(/\s+/g, ' ')

/** The number the way a manager types and reads it: 09xxxxxxxxx. */
export const shownPhone = (stored: string) =>
    toLocalMobile(stored) ?? stored ?? ''

export const formOf = (identity: IMemberIdentity): IdentityForm => ({
    firstName: identity.first_name ?? '',
    lastName: identity.last_name ?? '',
    phone: shownPhone(identity.phone_number),
})

/**
 * What the manager actually changed, or what is wrong with what they typed.
 *
 * Only real changes come back. An untouched field is left out of the request
 * altogether, so correcting a name can never rewrite the number, and saving
 * the form as it was opened sends nothing.
 */
export function identityChanges(
    current: IMemberIdentity,
    form: IdentityForm
): IdentityChanges {
    const changes: IMemberIdentityChanges = {}

    const firstName = tidy(form.firstName)
    const lastName = tidy(form.lastName)
    if (firstName.length > NAME_MAX) return { ok: false, error: 'first_name' }
    if (lastName.length > NAME_MAX) return { ok: false, error: 'last_name' }
    if (firstName !== (current.first_name ?? '')) changes.first_name = firstName
    if (lastName !== (current.last_name ?? '')) changes.last_name = lastName
    // A member with no name at all is listed as «کاربر بدون نام».
    if (
        (changes.first_name != null || changes.last_name != null) &&
        !firstName &&
        !lastName
    )
        return { ok: false, error: 'name' }

    // Compared as typed first: a stored number we cannot read as an Iranian
    // mobile is shown raw, and leaving it alone is not a change.
    const typed = form.phone.trim()
    if (typed !== shownPhone(current.phone_number)) {
        const phone = toLocalMobile(typed)
        if (!phone) return { ok: false, error: 'phone_number' }
        if (!isSameMobile(phone, current.phone_number))
            changes.phone_number = phone
    }

    return { ok: true, changes }
}
